import * as React from 'react';
import { Form, Select } from 'semantic-ui-react';

import capitalizar from 'src/modulos/Matriculas/IngresoNuevaMatricula/utils/capitalizar';

import Panel from 'src/modulos/Matriculas/IngresoNuevaMatricula/componentes/Panel';

import SeleccionableOtro from '../../../../SeleccionableOtro';

type Enseñanza = 'cie-hum' | 'tec-pro' | 'otro';
type CursoActual = 'basica' | '1medio' | '2medio' | '3medio' | '4medio';

interface FakeDB {
    enseñanza: { id: Enseñanza; texto: string }[];
    cursoActual: { id: CursoActual; texto: string }[];
}

const fakeDB: FakeDB = {
    enseñanza: [
        { id: 'cie-hum', texto: 'Científico/Humanista' },
        { id: 'tec-pro', texto: 'Técnico profesional' },
        { id: 'otro', texto: 'Otro' },
    ],
    cursoActual: [
        { id: 'basica', texto: 'Enseñanza básica' },
        { id: '1medio', texto: '1° Medio' },
        { id: '2medio', texto: '2° Medio' },
        { id: '3medio', texto: '3° Medio' },
        { id: '4medio', texto: '4° Medio' },
    ],
};

function mantenerSoloNumeros(texto: string) {
    if (texto.length <= 3) {
        return texto.replace(/[^0-9]/g, '');
    }
    return texto.slice(0, 3);
}

const egresadoCheckboxStyle = { marginTop: '4ex', marginBottom: '2.2ex' };
const psuRendidaCheckboxStyle = { marginTop: '2ex', marginBottom: '2ex' };

interface EnseñanzaDatos {
    id: Enseñanza;
    otro: string;
}

export interface Datos {
    colegio: string;
    egresado: boolean;
    cursoActual: CursoActual;
    enseñanza: EnseñanzaDatos;
    puntajes: {
        psuRendida: boolean;
        lenguaje: string;
        matematica: string;
        historia: string;
        ciencias: string;
        nem: string;
        ranking: string;
    };
}

export const inicial: Datos = {
    colegio: '',
    egresado: false,
    cursoActual: '4medio',
    enseñanza: {
        id: 'cie-hum',
        otro: '',
    },
    puntajes: {
        psuRendida: false,
        lenguaje: '',
        matematica: '',
        historia: '',
        ciencias: '',
        nem: '',
        ranking: '',
    },
};

interface Props {
    oculto: boolean;
    academicos: Datos;
    onChange(academicos: Datos): void;
}

class DatosAcademicos extends React.PureComponent<Props> {
    private readonly enseñanzaOptions = fakeDB.enseñanza.map(item => ({
        key: item.id,
        value: item.id,
        text: item.texto,
    }));

    private readonly cursoActualOptions = fakeDB.cursoActual.map(item => ({
        key: item.id,
        value: item.id,
        text: item.texto,
    }));

    public render() {
        const { oculto, academicos, onChange } = this.props;

        const etiquetaEnseñanzaOtro =
            academicos.enseñanza.id === 'tec-pro' ? 'Carrera' : 'Especifique';

        return (
            <Panel titulo='Datos académicos' oculto={oculto}>
                <Form.Input
                    label='Colegio de procedencia'
                    placeholder='Instituto Nacional'
                    value={academicos.colegio}
                    onChange={(_e, { value }) => {
                        onChange({
                            ...academicos,
                            colegio: capitalizar(value),
                        });
                    }}
                />

                <Form.Group>
                    <Form.Checkbox
                        label='¿Egresó de 4° medio?'
                        style={egresadoCheckboxStyle}
                        checked={academicos.egresado}
                        onChange={(_e, { checked }) => {
                            onChange({ ...academicos, egresado: checked || false });
                        }}
                        width='5'
                    />
                    <Form.Field hidden={academicos.egresado} width='11'>
                        <label>Curso actual</label>
                        <Select
                            options={this.cursoActualOptions}
                            value={academicos.cursoActual}
                            onChange={(_e, { value }) => {
                                onChange({ ...academicos, cursoActual: value as CursoActual });
                            }}
                        />
                    </Form.Field>
                </Form.Group>

                <SeleccionableOtro
                    select={{
                        etiqueta: 'Enseñanza',
                        opciones: this.enseñanzaOptions,
                        requerido: false,
                    }}
                    input={{
                        etiqueta: etiquetaEnseñanzaOtro,
                        mostrar: () => !oculto && academicos.enseñanza.id !== 'cie-hum',
                    }}
                    valor={academicos.enseñanza}
                    onChange={(enseñanza: EnseñanzaDatos) => {
                        onChange({ ...academicos, enseñanza });
                    }}
                />

                <Panel titulo='Puntajes'>
                    <Form.Checkbox
                        label='¿Ha rendido la PSU?'
                        style={psuRendidaCheckboxStyle}
                        checked={academicos.puntajes.psuRendida}
                        onChange={(_e, { checked }) => {
                            onChange({
                                ...academicos,
                                puntajes: {
                                    ...academicos.puntajes,
                                    psuRendida: checked || false,
                                },
                            });
                        }}
                    />

                    <p>Últimos puntajes que recuerda, de PSU o ensayos. Pueden ser aproximados.</p>

                    <Form.Group widths='6'>
                        <Form.Input
                            label='Lenguaje'
                            placeholder='400'
                            value={academicos.puntajes.lenguaje}
                            onChange={(_e, { value }) => {
                                onChange({
                                    ...academicos,
                                    puntajes: {
                                        ...academicos.puntajes,
                                        lenguaje: mantenerSoloNumeros(value),
                                    },
                                });
                            }}
                        />
                        <Form.Input
                            label='Matemática'
                            placeholder='600'
                            value={academicos.puntajes.matematica}
                            onChange={(_e, { value }) => {
                                onChange({
                                    ...academicos,
                                    puntajes: {
                                        ...academicos.puntajes,
                                        matematica: mantenerSoloNumeros(value),
                                    },
                                });
                            }}
                        />
                        <Form.Input
                            label='Historia'
                            placeholder='400'
                            value={academicos.puntajes.historia}
                            onChange={(_e, { value }) => {
                                onChange({
                                    ...academicos,
                                    puntajes: {
                                        ...academicos.puntajes,
                                        historia: mantenerSoloNumeros(value),
                                    },
                                });
                            }}
                        />
                        <Form.Input
                            label='Ciencias'
                            placeholder='600'
                            value={academicos.puntajes.ciencias}
                            onChange={(_e, { value }) => {
                                onChange({
                                    ...academicos,
                                    puntajes: {
                                        ...academicos.puntajes,
                                        ciencias: mantenerSoloNumeros(value),
                                    },
                                });
                            }}
                        />
                        <Form.Input
                            label='NEM'
                            placeholder='600'
                            value={academicos.puntajes.nem}
                            onChange={(_e, { value }) => {
                                onChange({
                                    ...academicos,
                                    puntajes: {
                                        ...academicos.puntajes,
                                        nem: mantenerSoloNumeros(value),
                                    },
                                });
                            }}
                        />
                        <Form.Input
                            label='Ranking'
                            placeholder='600'
                            value={academicos.puntajes.ranking}
                            onChange={(_e, { value }) => {
                                onChange({
                                    ...academicos,
                                    puntajes: {
                                        ...academicos.puntajes,
                                        ranking: mantenerSoloNumeros(value),
                                    },
                                });
                            }}
                        />
                    </Form.Group>
                </Panel>
            </Panel>
        );
    }
}

export default DatosAcademicos;



// WEBPACK FOOTER //
// ./src/modulos/Matriculas/IngresoNuevaMatricula/componentes/S3Alumnos/componentes/Alumno/componentes/DatosAcademicos.tsx